import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRole, Permission } from '../types/user.types';
import { UI_THEME } from '../ui/theme';
import permissoesService from '../services/permissoesService';

// ── Tipos ────────────────────────────────────────────────────────────────────

type TipoExecucao = 'DIRETA' | 'INDIRETA' | 'AMBAS';

interface Etapa {
  ordem: string;
  status: string;
  responsavel: string;
  acao: string;
  descricao: string;
  tipo: TipoExecucao;
  resultado: { ok: string; nok?: string };
  artefatos: string[];
  perfis: string[]; // quais perfis participam desta etapa
  cor: string; // cor da etapa
}

// ── Definição completa do fluxo ───────────────────────────────────────────────

const ETAPAS: Etapa[] = [
  {
    ordem: '1',
    status: 'Recebido',
    responsavel: 'Orçamento',
    acao: 'SOLICITAR_APROVACAO / criação',
    descricao: 'Emenda chega ao sistema via importação ou cadastro manual. Orçamento faz triagem inicial e verifica viabilidade orçamentária e limites por parlamentar.',
    tipo: 'AMBAS',
    resultado: { ok: 'Iniciar análise de admissibilidade', nok: 'Arquivar / aguardar ajuste' },
    artefatos: ['Dados básicos da emenda', 'Dotação orçamentária'],
    perfis: ['ORCAMENTO', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'blue',
  },
  {
    ordem: '2',
    status: 'Em análise de admissibilidade',
    responsavel: 'Orçamento',
    acao: 'INICIAR_ANALISE',
    descricao: 'Orçamento avalia se a emenda é formalmente admissível: verifica enquadramento legal, limites anuais do parlamentar e compatibilidade com o orçamento municipal.',
    tipo: 'AMBAS',
    resultado: { ok: 'Aprovar admissibilidade → define secretariaDestino', nok: 'Devolver ao legislativo com justificativa' },
    artefatos: ['Parecer técnico/orçamentário', 'Apontamento de incompatibilidade (se negar)'],
    perfis: ['ORCAMENTO', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'amber',
  },
  {
    ordem: '3',
    status: 'Admissibilidade aprovada',
    responsavel: 'Orçamento → Secretaria',
    acao: 'APROVAR_ADMISSIBILIDADE',
    descricao: 'Admissibilidade aprovada pelo Orçamento. A secretaria de destino é definida nesta etapa. A emenda é encaminhada à Secretaria para análise de demanda.',
    tipo: 'AMBAS',
    resultado: { ok: 'Secretaria inicia análise de demanda', nok: '—' },
    artefatos: ['Secretaria de destino definida'],
    perfis: ['ORCAMENTO', 'SECRETARIA', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'emerald',
  },
  {
    ordem: '3b',
    status: 'Devolvida ao legislativo',
    responsavel: 'Orçamento',
    acao: 'REPROVAR_ADMISSIBILIDADE',
    descricao: 'Admissibilidade reprovada. A emenda retorna ao legislativo (câmara) com justificativa detalhada dos pontos não atendidos.',
    tipo: 'AMBAS',
    resultado: { ok: 'Fim do fluxo (pode ser reaberta)' },
    artefatos: ['Justificativa de inviabilidade'],
    perfis: ['ORCAMENTO', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'red',
  },
  {
    ordem: '4',
    status: 'Em análise de demanda',
    responsavel: 'Secretaria',
    acao: 'INICIAR_ANALISE_DEMANDA',
    descricao: 'Secretaria analisa a viabilidade técnica e operacional da demanda: capacidade de execução, prioridade, alinhamento com o plano municipal e legislação vigente (IN).',
    tipo: 'AMBAS',
    resultado: { ok: 'Aprovar demanda', nok: 'Devolver por incompatibilidade com justificativa' },
    artefatos: ['Justificativa de viabilidade', 'Requisitos operacionais/territoriais', 'Pontos não atendidos conforme IN (se negar)'],
    perfis: ['SECRETARIA', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'teal',
  },
  {
    ordem: '5',
    status: 'Análise de demanda aprovada',
    responsavel: 'Secretaria',
    acao: 'APROVAR_DEMANDA',
    descricao: 'Demanda aprovada. O roteamento depende do tipo de execução: Direta segue para execução sem etapa documental; Indireta vai para Convênios para análise documental.',
    tipo: 'AMBAS',
    resultado: { ok: 'Direta → Iniciado/Execução | Indireta → Convênios (documental)' },
    artefatos: ['Decisão de encaminhamento (Direta/Indireta)'],
    perfis: ['SECRETARIA', 'CONVENIOS', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'emerald',
  },
  {
    ordem: '5b',
    status: 'Devolvida por incompatibilidade de demanda',
    responsavel: 'Secretaria',
    acao: 'REPROVAR_DEMANDA',
    descricao: 'Demanda reprovada por incompatibilidade técnica ou operacional. Retorna ao Orçamento com justificativa dos pontos não atendidos conforme IN.',
    tipo: 'AMBAS',
    resultado: { ok: 'Retorna a Orçamento para ajustes' },
    artefatos: ['Justificativa dos pontos não atendidos (conforme IN)'],
    perfis: ['SECRETARIA', 'ORCAMENTO', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'red',
  },
  {
    ordem: '6',
    status: 'Em análise documental',
    responsavel: 'Convênios',
    acao: 'INICIAR_ANALISE_DOCUMENTAL',
    descricao: 'Exclusivo para execução Indireta. Convênios verifica toda a documentação da OSC/proponente: regularidade fiscal, estatuto, ata, certidões, plano de trabalho e demais exigências do termo de fomento.',
    tipo: 'INDIRETA',
    resultado: { ok: 'Aprovar documental → formalização', nok: 'Devolver por inviabilidade com checklist' },
    artefatos: ['Checklist de documentos do termo/convênio', 'Comprovações da OSC', 'Certidões negativas'],
    perfis: ['CONVENIOS', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'violet',
  },
  {
    ordem: '7',
    status: 'Análise documental aprovada',
    responsavel: 'Convênios',
    acao: 'APROVAR_DOCUMENTAL',
    descricao: 'Documentação aprovada. Convênios formaliza o termo de fomento/convênio e registra o empenho para início da execução.',
    tipo: 'INDIRETA',
    resultado: { ok: 'Formalização do termo e empenho → Iniciado' },
    artefatos: ['Minuta do termo de fomento', 'Empenho registrado'],
    perfis: ['CONVENIOS', 'JURIDICO', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'emerald',
  },
  {
    ordem: '7b',
    status: 'Devolvida por inviabilidade documental',
    responsavel: 'Convênios',
    acao: 'REPROVAR_DOCUMENTAL',
    descricao: 'Documentação reprovada. Retorna ao Orçamento/Secretaria com checklist dos documentos faltantes ou irregulares.',
    tipo: 'INDIRETA',
    resultado: { ok: 'Retorna a Orçamento/Secretaria com ajustes' },
    artefatos: ['Checklist de pendências documentais'],
    perfis: ['CONVENIOS', 'ORCAMENTO', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'red',
  },
  {
    ordem: '8',
    status: 'Iniciado',
    responsavel: 'Convênios (Indireta) / Secretaria (Direta)',
    acao: 'AGUARDAR_DETALHAMENTO',
    descricao: 'Emenda iniciada formalmente. Sinaliza ordem de início e empenho. O proponente/operador envia detalhamento, cronograma e documentos complementares.',
    tipo: 'AMBAS',
    resultado: { ok: 'Liquidação → Em execução' },
    artefatos: ['Ordem de início', 'Empenho', 'Cronograma de execução'],
    perfis: ['CONVENIOS', 'SECRETARIA', 'OPERADOR', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'indigo',
  },
  {
    ordem: '9',
    status: 'Em execução',
    responsavel: 'Execução financeira / Operador',
    acao: '(liquidação financeira)',
    descricao: 'Emenda em execução física e financeira. Medições, liquidações e pagamentos parciais são registrados. Convênios acompanha o andamento.',
    tipo: 'AMBAS',
    resultado: { ok: 'Pagamento total → Concluído' },
    artefatos: ['Medições/liquidações', 'Relatórios de execução', 'Notas fiscais'],
    perfis: ['CONVENIOS', 'SECRETARIA', 'OPERADOR', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'indigo',
  },
  {
    ordem: '10',
    status: 'Concluído',
    responsavel: 'Execução financeira',
    acao: 'APROVAR',
    descricao: 'Pagamento total efetuado. Ciclo de vida encerrado. Pode exigir prestação de contas e documentos de encerramento.',
    tipo: 'AMBAS',
    resultado: { ok: 'Fim de ciclo / Prestação de contas' },
    artefatos: ['Comprovantes de pagamento', 'Prestação de contas (se Indireta)', 'Documentos de encerramento'],
    perfis: ['CONVENIOS', 'SECRETARIA', 'OPERADOR', 'ADMIN', 'ORCAMENTO', 'CAMARA', 'VEREADOR'],
    cor: 'emerald',
  },
];

// ── Mapa de cores ─────────────────────────────────────────────────────────────

const COR: Record<string, { bg: string; border: string; text: string; dot: string; badge: string }> = {
  blue:    { bg: 'bg-blue-50 dark:bg-blue-950/30',       border: 'border-blue-200 dark:border-blue-500/40',       text: 'text-blue-900 dark:text-blue-200',       dot: 'bg-blue-500',    badge: 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200' },
  amber:   { bg: 'bg-amber-50 dark:bg-amber-950/30',     border: 'border-amber-200 dark:border-amber-500/40',     text: 'text-amber-900 dark:text-amber-200',     dot: 'bg-amber-500',   badge: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200' },
  emerald: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-200 dark:border-emerald-500/40', text: 'text-emerald-900 dark:text-emerald-200', dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200' },
  red:     { bg: 'bg-red-50 dark:bg-red-950/30',         border: 'border-red-200 dark:border-red-500/40',         text: 'text-red-900 dark:text-red-200',         dot: 'bg-red-500',     badge: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200' },
  teal:    { bg: 'bg-teal-50 dark:bg-teal-950/30',       border: 'border-teal-200 dark:border-teal-500/40',       text: 'text-teal-900 dark:text-teal-200',       dot: 'bg-teal-500',    badge: 'bg-teal-100 text-teal-800 dark:bg-teal-950/50 dark:text-teal-200' },
  violet:  { bg: 'bg-violet-50 dark:bg-violet-950/30',   border: 'border-violet-200 dark:border-violet-500/40',   text: 'text-violet-900 dark:text-violet-200',   dot: 'bg-violet-500',  badge: 'bg-violet-100 text-violet-800 dark:bg-violet-950/50 dark:text-violet-200' },
  indigo:  { bg: 'bg-indigo-50 dark:bg-indigo-950/30',   border: 'border-indigo-200 dark:border-indigo-500/40',   text: 'text-indigo-900 dark:text-indigo-200',   dot: 'bg-indigo-500',  badge: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200' },
};

const PERFIL_LABEL: Record<string, string> = {
  ORCAMENTO: 'Orçamento',
  SECRETARIA: 'Secretaria',
  CONVENIOS: 'Convênios',
  JURIDICO: 'Jurídico',
  OPERADOR: 'Operador',
  ADMIN: 'Admin',
};

const PERFIL_COR: Record<string, string> = {
  ORCAMENTO: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-500/40',
  SECRETARIA: 'bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-950/40 dark:text-teal-200 dark:border-teal-500/40',
  CONVENIOS: 'bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-950/40 dark:text-violet-200 dark:border-violet-500/40',
  JURIDICO: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-200 dark:border-blue-500/40',
  OPERADOR: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700',
  ADMIN: 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-200 dark:border-rose-500/40',
};

// ── Etapas do Plano de Trabalho ───────────────────────────────────────────────

interface EtapaPT {
  ordem: string;
  status: string;
  label: string;
  descricao: string;
  perfis: string[];
  cor: string;
}

const ETAPAS_PT: EtapaPT[] = [
  {
    ordem: '1',
    status: 'ENVIO',
    label: 'Envio pela Instituição',
    descricao: 'Instituição envia o plano de trabalho para análise. Gestor e Operador podem submeter e acompanhar.',
    perfis: ['SECRETARIA', 'OPERADOR', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'blue',
  },
  {
    ordem: '2',
    status: 'ORCAMENTO',
    label: 'Análise — Orçamento',
    descricao: 'Orçamento verifica viabilidade orçamentária e compatibilidade com a emenda antes de encaminhar a Convênios.',
    perfis: ['ORCAMENTO', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'amber',
  },
  {
    ordem: '3',
    status: 'CONVENIOS_ANALISE',
    label: 'Análise — Convênios',
    descricao: 'Convênios analisa a documentação inicial, vincula Gestor da Parceria e encaminha ao Gestor.',
    perfis: ['CONVENIOS', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'violet',
  },
  {
    ordem: '4',
    status: 'GESTOR_PARCERIA',
    label: 'Gestor da Parceria',
    descricao: 'Gestor vinculado analisa e aprova o plano. Pode devolver para correções ou encaminhar à Comissão de Seleção.',
    perfis: ['SECRETARIA', 'CONVENIOS', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'teal',
  },
  {
    ordem: '5',
    status: 'COMISSAO_SELECAO',
    label: 'Comissão de Seleção',
    descricao: 'Comissão avalia o plano, delibera e registra a ATA. Pode aprovar ou devolver para ajustes.',
    perfis: ['SECRETARIA', 'CONVENIOS', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'indigo',
  },
  {
    ordem: '6',
    status: 'CONVENIOS_MINUTA',
    label: 'Minuta — Convênios',
    descricao: 'Convênios elabora a minuta do termo de fomento/colaboração e encaminha à Procuradoria Geral Municipal.',
    perfis: ['CONVENIOS', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'violet',
  },
  {
    ordem: '7',
    status: 'PROCURADORIA_JURIDICA',
    label: 'Procuradoria Geral Municipal',
    descricao: 'Procurador jurídico (Jurídico/Gestor Jurídico) emite parecer sobre a minuta do termo.',
    perfis: ['JURIDICO', 'JURIDICO_AUXILIAR', 'GESTOR_JURIDICO', 'CONVENIOS', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'blue',
  },
  {
    ordem: '8',
    status: 'CONVENIOS_FORMALIZACAO',
    label: 'Formalização — Convênios',
    descricao: 'Convênios formaliza o termo assinado e registra o encerramento do fluxo do plano de trabalho.',
    perfis: ['CONVENIOS', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'emerald',
  },
  {
    ordem: '9',
    status: 'CONCLUIDO',
    label: 'Concluído',
    descricao: 'Plano de trabalho concluído. Termo formalizado e registrado.',
    perfis: ['CONVENIOS', 'SECRETARIA', 'OPERADOR', 'ORCAMENTO', 'ADMIN', 'CAMARA', 'VEREADOR'],
    cor: 'emerald',
  },
];

// ── Labels e grupos de permissões ─────────────────────────────────────────────

const ROLE_LABELS_FULL: Record<UserRole, string> = {
  [UserRole.ADMIN]:            'Admin',
  [UserRole.ORCAMENTO]:        'Orçamento',
  [UserRole.SECRETARIA]:       'Secretaria',
  [UserRole.CONVENIOS]:        'Convênios',
  [UserRole.OPERADOR]:         'Operador',
  [UserRole.GESTOR_JURIDICO]:  'Gestor Jurídico',
  [UserRole.ANALISTA]:         'Analista',
  [UserRole.JURIDICO]:         'Jurídico',
  [UserRole.JURIDICO_AUXILIAR]: 'Auxiliar da Procuradoria',
  [UserRole.CAMARA]:           'Câmara',
  [UserRole.VEREADOR]:         'Parlamentar',
};

const PERMISSION_LABELS: Record<Permission, string> = {
  [Permission.VIEW_EMENDAS]:               'Ver Emendas',
  [Permission.CREATE_EMENDA]:              'Criar Emenda',
  [Permission.EDIT_EMENDA]:               'Editar Emenda',
  [Permission.APPROVE_ADMISSIBILIDADE]:   'Aprovar Admissibilidade',
  [Permission.MANAGE_DOTACAO_ORCAMENTARIA]: 'Gerenciar Dotação Orçamentária',
  [Permission.VIEW_EXECUCAO_FINANCEIRA]: 'Ver Execução Financeira',
  [Permission.REGISTER_MOVIMENTACAO_FINANCEIRA]: 'Registrar Movimentação Financeira',
  [Permission.ANULAR_MOVIMENTACAO_FINANCEIRA]: 'Anular Movimentação Financeira',
  [Permission.VIEW_INSTITUTIONS]:         'Ver Instituições',
  [Permission.CREATE_INSTITUTION]:        'Criar Instituição',
  [Permission.EDIT_INSTITUTION]:          'Editar Instituição',
  [Permission.MANAGE_INSTITUTION_DOCS]:   'Gerenciar Documentos',
  [Permission.MANAGE_INSTITUTION_DIRIGENTES]: 'Gerenciar Dirigentes',
  [Permission.APPROVE_INSTITUTION]:       'Aprovar Instituição',
  [Permission.VIEW_COUNCILORS]:           'Ver Parlamentares',
  [Permission.CREATE_COUNCILOR]:          'Criar Parlamentar',
  [Permission.EDIT_COUNCILOR]:            'Editar Parlamentar',
  [Permission.DELETE_COUNCILOR]:          'Excluir Parlamentar',
  [Permission.VIEW_USERS]:               'Ver Usuários',
  [Permission.CREATE_USER]:              'Criar Usuário',
  [Permission.EDIT_USER]:               'Editar Usuário',
  [Permission.DELETE_USER]:             'Excluir Usuário',
  [Permission.APPROVE_USER]:            'Aprovar Usuário',
  [Permission.VIEW_EMENDA_REPORTS]:     'Relatórios de Emendas',
  [Permission.VIEW_INSTITUTION_REPORTS]:'Relatórios de Instituições',
  [Permission.EXPORT_DATA]:             'Exportar Dados',
  [Permission.VIEW_PROFILE]:            'Ver Perfil',
  [Permission.EDIT_PROFILE]:            'Editar Perfil',
  [Permission.VIEW_GESTAO_PLANOS]:           'Ver Planos de Trabalho',
  [Permission.CREATE_PLANO_TRABALHO]:        'Criar plano de trabalho',
  [Permission.VIEW_PRESTACAO_CONTAS]:        'Ver Prestação de Contas',
  [Permission.MANAGE_COMISSOES]:             'Gerenciar Comissões',
  [Permission.INICIAR_ANALISE_INSTITUICAO]:  'Iniciar Análise de Instituição',
  [Permission.WORKFLOW_DECISION_PLANO]:      'Tomar Decisão no Workflow',
  [Permission.UPLOAD_RETRO_ANEXO]:           'Upload Retroativo de Anexo',
  [Permission.ASSIGN_PROCURADOR]:            'Atribuir Procurador',
  [Permission.ACCESS_FILA_JURIDICA]:         'Acessar Fila Jurídica',
};

const PERMISSION_GROUPS: { label: string; permissions: Permission[] }[] = [
  { label: 'Emendas',           permissions: [Permission.VIEW_EMENDAS, Permission.CREATE_EMENDA, Permission.EDIT_EMENDA, Permission.APPROVE_ADMISSIBILIDADE, Permission.MANAGE_DOTACAO_ORCAMENTARIA] },
  { label: 'Execução Financeira', permissions: [Permission.VIEW_EXECUCAO_FINANCEIRA, Permission.REGISTER_MOVIMENTACAO_FINANCEIRA, Permission.ANULAR_MOVIMENTACAO_FINANCEIRA] },
  { label: 'Planos de Trabalho',permissions: [Permission.VIEW_GESTAO_PLANOS, Permission.CREATE_PLANO_TRABALHO, Permission.VIEW_PRESTACAO_CONTAS, Permission.WORKFLOW_DECISION_PLANO, Permission.UPLOAD_RETRO_ANEXO] },
  { label: 'Instituições',      permissions: [Permission.VIEW_INSTITUTIONS, Permission.CREATE_INSTITUTION, Permission.EDIT_INSTITUTION, Permission.MANAGE_INSTITUTION_DOCS, Permission.MANAGE_INSTITUTION_DIRIGENTES, Permission.APPROVE_INSTITUTION, Permission.INICIAR_ANALISE_INSTITUICAO] },
  { label: 'Parlamentares',     permissions: [Permission.VIEW_COUNCILORS, Permission.CREATE_COUNCILOR, Permission.EDIT_COUNCILOR, Permission.DELETE_COUNCILOR] },
  { label: 'Usuários',          permissions: [Permission.VIEW_USERS, Permission.CREATE_USER, Permission.EDIT_USER, Permission.DELETE_USER, Permission.APPROVE_USER] },
  { label: 'Relatórios',        permissions: [Permission.VIEW_EMENDA_REPORTS, Permission.VIEW_INSTITUTION_REPORTS, Permission.EXPORT_DATA] },
  { label: 'Sistema',           permissions: [Permission.MANAGE_COMISSOES, Permission.ASSIGN_PROCURADOR, Permission.ACCESS_FILA_JURIDICA] },
  { label: 'Perfil',            permissions: [Permission.VIEW_PROFILE, Permission.EDIT_PROFILE] },
];

const ALL_ROLES = Object.values(UserRole);

// ── Componente ────────────────────────────────────────────────────────────────

const WorkflowPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role ?? '';

  const [activeTab, setActiveTab] = useState<'fluxo' | 'permissoes'>('fluxo');
  const [filtroTipo, setFiltroTipo] = useState<'AMBAS' | 'DIRETA' | 'INDIRETA'>('AMBAS');
  const [filtroPerfil, setFiltroPerfil] = useState<string>(role || 'TODOS');
  const [expandida, setExpandida] = useState<string | null>(null);

  // Matriz papel→permissão: sempre lida do backend (role_permission), nunca de uma
  // cópia local — senão esta tela mostraria um estado desatualizado assim que alguém
  // editasse permissões em PermissoesConfigPage.
  const [rolePermissions, setRolePermissions] = useState<Record<string, string[]> | null>(null);
  const [rolePermissionsError, setRolePermissionsError] = useState(false);

  const fetchRolePermissions = useCallback(() => {
    setRolePermissionsError(false);
    permissoesService
      .getAll()
      .then(res => setRolePermissions(res.data))
      .catch(() => setRolePermissionsError(true));
  }, []);

  useEffect(() => {
    fetchRolePermissions();
  }, [fetchRolePermissions]);
  const isSaas = UI_THEME === 'saas';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const headerClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const panelClass = 'rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const mutedTextClass = 'text-slate-500 dark:text-slate-400';
  const tabActiveClass = 'bg-white text-slate-950 shadow-sm dark:bg-slate-950 dark:text-slate-50';
  const tabInactiveClass = 'text-slate-600 hover:text-slate-950 dark:text-slate-300 dark:hover:text-white';
  const filterActiveClass = isSaas
    ? 'border-teal-600 bg-teal-600 text-white'
    : 'border-blue-700 bg-blue-700 text-white';
  const filterInactiveClass = 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800';
  const tableSkinClass = '[&_table]:min-w-full [&_table]:text-xs [&_th]:border [&_th]:border-slate-200 [&_th]:bg-slate-50 [&_th]:text-slate-700 dark:[&_th]:border-slate-700 dark:[&_th]:bg-slate-800 dark:[&_th]:text-slate-200 [&_td]:border [&_td]:border-slate-200 [&_td]:text-slate-700 dark:[&_td]:border-slate-700 dark:[&_td]:text-slate-300 [&_tr:hover]:bg-slate-50 dark:[&_tr:hover]:bg-slate-800/60';

  const etapasFiltradas = ETAPAS.filter(e => {
    const tipoOk = filtroTipo === 'AMBAS' || e.tipo === 'AMBAS' || e.tipo === filtroTipo;
    const perfilOk = filtroPerfil === 'TODOS' || e.perfis.includes(filtroPerfil);
    return tipoOk && perfilOk;
  });

  const toggle = (ordem: string) => setExpandida(v => v === ordem ? null : ordem);

  return (
    <div className="w-full space-y-6">

      <div className={`rounded-2xl border p-5 ${headerClass}`}>
        <p className={`text-xs font-semibold uppercase tracking-[0.22em] ${accentTextClass}`}>
          Fluxo de tramitação
        </p>
        <h1 className="mt-2 text-2xl font-bold text-slate-950 dark:text-slate-50">Tramitação de emendas</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          Ciclo de vida completo das emendas por perfil - execução direta e indireta.
        </p>
      </div>

      <div className="flex w-fit rounded-2xl bg-slate-100 p-1 dark:bg-slate-800">
        <button
          type="button"
          onClick={() => setActiveTab('fluxo')}
          className={`rounded-2xl px-4 py-2 text-sm font-medium transition ${
            activeTab === 'fluxo'
              ? tabActiveClass
              : tabInactiveClass
          }`}
        >
          Fluxo
        </button>
        {role === UserRole.ADMIN && (
          <button
            type="button"
            onClick={() => setActiveTab('permissoes')}
            className={`rounded-2xl px-4 py-2 text-sm font-medium transition ${
              activeTab === 'permissoes'
                ? tabActiveClass
                : tabInactiveClass
            }`}
          >
            Permissões
          </button>
        )}
      </div>

      {activeTab === 'fluxo' && <>

      {/* ── Filtros ── */}
      <div className={`${panelClass} flex flex-wrap items-end gap-4 p-4`}>

        <div>
          <p className={`mb-1.5 text-xs font-semibold uppercase tracking-[0.18em] ${mutedTextClass}`}>Tipo de execução</p>
          <div className="flex gap-1.5">
            {(['AMBAS', 'DIRETA', 'INDIRETA'] as const).map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setFiltroTipo(t)}
                className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-colors ${
                  filtroTipo === t
                    ? filterActiveClass
                    : filterInactiveClass
                }`}
              >
                {t === 'AMBAS' ? 'Todas' : t.charAt(0) + t.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className={`mb-1.5 text-xs font-semibold uppercase tracking-[0.18em] ${mutedTextClass}`}>Perfil</p>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setFiltroPerfil('TODOS')}
              className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-colors ${
                filtroPerfil === 'TODOS'
                  ? filterActiveClass
                  : filterInactiveClass
              }`}
            >
              Todos
            </button>
            {Object.keys(PERFIL_LABEL).map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setFiltroPerfil(p)}
                className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-colors ${
                  filtroPerfil === p
                    ? filterActiveClass
                    : `${PERFIL_COR[p]} hover:opacity-80`
                }`}
              >
                {PERFIL_LABEL[p]}
                {p === role && ' (você)'}
              </button>
            ))}
          </div>
        </div>

      </div>

      {/* ── Legenda ── */}
      {role && (
        <div className={`flex items-center gap-2 rounded-2xl border p-3 text-sm ${PERFIL_COR[role] ?? 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'}`}>
          <span>
            Você está logado como <strong>{PERFIL_LABEL[role] ?? role}</strong>.
            {filtroPerfil === 'TODOS'
              ? ' Use os filtros de perfil para ver apenas suas etapas.'
              : filtroPerfil === role
                ? ' Exibindo apenas as etapas do seu perfil.'
                : ` Visualizando etapas do perfil: ${PERFIL_LABEL[filtroPerfil] ?? filtroPerfil}.`}
          </span>
          {filtroPerfil !== role && (
            <button
              type="button"
              onClick={() => setFiltroPerfil(role)}
              className="ml-auto whitespace-nowrap text-xs font-semibold underline"
            >
              Ver meu fluxo
            </button>
          )}
        </div>
      )}

      {/* ── Linha do tempo ── */}
      <div className="relative">
        {/* Linha vertical */}
        <div className="absolute bottom-0 left-5 top-0 z-0 w-0.5 bg-slate-200 dark:bg-slate-700" />

        <div className="space-y-3">
          {etapasFiltradas.map((etapa, idx) => {
            const c = COR[etapa.cor] ?? COR.blue;
            const aberta = expandida === etapa.ordem;
            const euParticipo = etapa.perfis.includes(role);

            return (
              <div key={etapa.ordem} className="relative pl-14">
                {/* Dot */}
                <div className={`absolute left-3.5 top-4 z-10 h-4 w-4 rounded-full border-2 border-white dark:border-slate-950 ${c.dot} ${euParticipo ? 'ring-2 ring-slate-400 ring-offset-1 dark:ring-offset-slate-900' : ''}`} />

                {/* Número */}
                <div className="absolute left-0 top-3 w-7 text-center text-[10px] font-bold text-slate-400">
                  {etapa.ordem}
                </div>

                {/* Card */}
                <div className={`overflow-hidden rounded-2xl border bg-white dark:bg-slate-900 ${c.border} ${euParticipo ? 'shadow-md' : 'shadow-sm opacity-90'}`}>

                  {/* Cabeçalho clicável */}
                  <button
                    type="button"
                    onClick={() => toggle(etapa.ordem)}
                    className={`w-full text-left px-4 py-3 ${c.bg} flex items-start justify-between gap-3`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`text-sm font-semibold ${c.text}`}>{etapa.status}</span>
                        {etapa.tipo !== 'AMBAS' && (
                          <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] font-bold text-white">
                            {etapa.tipo}
                          </span>
                        )}
                        {euParticipo && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-600 text-white">
                            sua etapa
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {etapa.perfis.filter(p => p !== 'ADMIN').map(p => (
                          <span key={p} className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${PERFIL_COR[p] ?? 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                            {PERFIL_LABEL[p] ?? p}
                          </span>
                        ))}
                      </div>
                    </div>
                    <svg className={`mt-0.5 h-4 w-4 shrink-0 text-slate-500 transition-transform ${aberta ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {/* Corpo expandido */}
                  {aberta && (
                    <div className="space-y-4 border-t border-slate-200 bg-white px-4 py-4 text-sm dark:border-slate-700 dark:bg-slate-900">

                      <div>
                        <p className="mb-1 text-xs font-semibold uppercase text-slate-400">Descrição</p>
                        <p className="leading-relaxed text-slate-700 dark:text-slate-300">{etapa.descricao}</p>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <p className="mb-1 text-xs font-semibold uppercase text-slate-400">Responsável</p>
                          <p className="font-medium text-slate-800 dark:text-slate-200">{etapa.responsavel}</p>
                        </div>
                        <div>
                          <p className="mb-1 text-xs font-semibold uppercase text-slate-400">Ação no sistema</p>
                          <code className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200">{etapa.acao}</code>
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <p className="mb-1 text-xs font-semibold uppercase text-slate-400">Se aprovado</p>
                          <p className="text-emerald-700">{etapa.resultado.ok}</p>
                        </div>
                        {etapa.resultado.nok && (
                          <div>
                            <p className="mb-1 text-xs font-semibold uppercase text-slate-400">Se negado</p>
                            <p className="text-red-700">{etapa.resultado.nok}</p>
                          </div>
                        )}
                      </div>

                      {etapa.artefatos.length > 0 && (
                        <div>
                          <p className="mb-1 text-xs font-semibold uppercase text-slate-400">Artefatos / Documentos</p>
                          <ul className="list-inside list-disc space-y-0.5 text-slate-700 dark:text-slate-300">
                            {etapa.artefatos.map((a, i) => <li key={i}>{a}</li>)}
                          </ul>
                        </div>
                      )}

                      {/* Atalho para emendas nesse status */}
                      <div className="border-t border-slate-200 pt-2 dark:border-slate-700">
                        <button
                          type="button"
                          onClick={() => navigate(`/dashboard/emendas?etapa=${encodeURIComponent(etapa.status)}`)}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-200"
                        >
                          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/>
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0z" clipRule="evenodd"/>
                          </svg>
                          Ver emendas na etapa "{etapa.status}"
                        </button>
                      </div>

                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {etapasFiltradas.length === 0 && (
        <div className="py-16 text-center text-slate-400">
          <p className="font-semibold">Nenhuma etapa encontrada para os filtros selecionados.</p>
        </div>
      )}

      </>}

      {/* ── Aba Permissões (ADMIN only) ── */}
      {activeTab === 'permissoes' && role === UserRole.ADMIN && (
        <div className="space-y-8">

          {/* ── Visibilidade por fase da Emenda ── */}
          <div className={`${panelClass} ${tableSkinClass} space-y-2 p-4`}>
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Visibilidade por fase - Emenda</h2>
            <p className={`text-xs ${mutedTextClass}`}>
              Quais fases cada perfil pode ver e em que tipo de execução.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table className="text-xs border-collapse">
                <thead>
                  <tr style={{ verticalAlign: 'bottom' }}>
                    <th className="sticky left-0 z-10 bg-white border border-gray-200 px-3 py-2 text-left font-semibold text-gray-700 min-w-[220px]">
                      Fase
                    </th>
                    <th className="border border-gray-200 px-2 pb-2 text-center font-medium text-gray-500 bg-slate-50 whitespace-nowrap">
                      Execução
                    </th>
                    {ALL_ROLES.map(r => (
                      <th
                        key={r}
                        className="border border-gray-200 bg-slate-50 text-gray-600 font-medium"
                        style={{ width: 32 }}
                      >
                        <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', padding: '8px 6px', whiteSpace: 'nowrap', fontSize: 11 }}>
                          {ROLE_LABELS_FULL[r]}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ETAPAS.map(etapa => {
                    const c = COR[etapa.cor] ?? COR.blue;
                    return (
                      <tr key={etapa.ordem} className="hover:bg-slate-50">
                        <td className={`sticky left-0 z-10 border border-gray-200 px-3 py-1.5 font-medium whitespace-nowrap ${c.bg} ${c.text}`}>
                          <span className="text-gray-400 mr-1.5">{etapa.ordem}.</span>
                          {etapa.status}
                        </td>
                        <td className="border border-gray-200 px-2 py-1.5 text-center">
                          {etapa.tipo === 'AMBAS' ? (
                            <span className="text-[10px] text-gray-500">Ambas</span>
                          ) : (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-800 text-white">
                              {etapa.tipo}
                            </span>
                          )}
                        </td>
                        {ALL_ROLES.map(r => {
                          const participa = etapa.perfis.includes(r);
                          return (
                            <td key={r} className="border border-gray-200 px-2 py-1.5 text-center">
                              {participa ? (
                                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full font-bold ${c.badge}`}>✓</span>
                              ) : (
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-gray-300">·</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Visibilidade por fase do Plano de Trabalho ── */}
          <div className={`${panelClass} ${tableSkinClass} space-y-2 p-4`}>
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Visibilidade por fase - Plano de Trabalho</h2>
            <p className={`text-xs ${mutedTextClass}`}>
              Quais perfis participam de cada etapa do fluxo de convênios (plano de trabalho).
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table className="text-xs border-collapse">
                <thead>
                  <tr style={{ verticalAlign: 'bottom' }}>
                    <th className="sticky left-0 z-10 bg-white border border-gray-200 px-3 py-2 text-left font-semibold text-gray-700 min-w-[220px]">
                      Fase
                    </th>
                    {ALL_ROLES.map(r => (
                      <th
                        key={r}
                        className="border border-gray-200 bg-slate-50 text-gray-600 font-medium"
                        style={{ width: 32 }}
                      >
                        <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', padding: '8px 6px', whiteSpace: 'nowrap', fontSize: 11 }}>
                          {ROLE_LABELS_FULL[r]}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ETAPAS_PT.map(etapa => {
                    const c = COR[etapa.cor] ?? COR.blue;
                    return (
                      <tr key={etapa.ordem} className="hover:bg-slate-50">
                        <td className={`sticky left-0 z-10 border border-gray-200 px-3 py-1.5 font-medium whitespace-nowrap ${c.bg} ${c.text}`}>
                          <span className="text-gray-400 mr-1.5">{etapa.ordem}.</span>
                          {etapa.label}
                        </td>
                        {ALL_ROLES.map(r => {
                          const participa = etapa.perfis.includes(r);
                          return (
                            <td key={r} className="border border-gray-200 px-2 py-1.5 text-center">
                              {participa ? (
                                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full font-bold ${c.badge}`}>✓</span>
                              ) : (
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-gray-300">·</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Permissões do sistema ── */}
          <div className={`${panelClass} ${tableSkinClass} space-y-2 p-4`}>
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Permissões do sistema</h2>
            <p className={`text-xs ${mutedTextClass}`}>
              Matriz completa de permissões por perfil, lida diretamente do backend.
            </p>
          {rolePermissionsError ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <p className={`text-sm ${mutedTextClass}`}>Não foi possível carregar as permissões configuradas no backend.</p>
              <button
                type="button"
                onClick={fetchRolePermissions}
                className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500"
              >
                Tentar novamente
              </button>
            </div>
          ) : !rolePermissions ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-blue-700 border-t-transparent dark:border-blue-400" />
            </div>
          ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="text-xs border-collapse">
              <thead>
                <tr style={{ verticalAlign: 'bottom' }}>
                  <th className="sticky left-0 z-10 bg-white border border-gray-200 px-3 py-2 text-left font-semibold text-gray-700 min-w-[180px]">
                    Permissão
                  </th>
                  {ALL_ROLES.map(r => (
                    <th
                      key={r}
                      className="border border-gray-200 bg-slate-50 text-gray-600 font-medium"
                      style={{ width: 32 }}
                    >
                      <div style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', padding: '8px 6px', whiteSpace: 'nowrap', fontSize: 11 }}>
                        {ROLE_LABELS_FULL[r]}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_GROUPS.map(group => (
                  <React.Fragment key={group.label}>
                    <tr>
                      <td
                        colSpan={ALL_ROLES.length + 1}
                        className="border border-gray-200 px-3 py-1 bg-slate-100 font-semibold text-slate-600 text-[11px] uppercase tracking-wide"
                      >
                        {group.label}
                      </td>
                    </tr>
                    {group.permissions.map(perm => (
                      <tr key={perm} className="hover:bg-slate-50">
                        <td className="sticky left-0 z-10 bg-white border border-gray-200 px-3 py-1.5 text-gray-700 font-medium whitespace-nowrap">
                          {PERMISSION_LABELS[perm]}
                        </td>
                        {ALL_ROLES.map(r => {
                          const has = (rolePermissions[r] ?? []).includes(perm);
                          return (
                            <td
                              key={r}
                              className="border border-gray-200 px-2 py-1.5 text-center"
                            >
                              {has ? (
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 font-bold">✓</span>
                              ) : (
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 text-gray-400">✗</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          )}
          </div>

        </div>
      )}

    </div>
  );
};

export default WorkflowPage;



